import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useUpdateProfile } from '@/api/hooks/useAccount';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  LayoutDashboard,
  CalendarDays,
  Monitor,
  Users,
  Package,
  Bell,
  DollarSign,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Calendar,
  RotateCcw,
  Shield,
  CreditCard,
  Globe,
  ShieldAlert,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { useIncidents } from '@/api/hooks/useAdminIncidents';

const NAV_ITEMS = [
  { key: 'dashboard', path: '/admin', icon: LayoutDashboard },
  { key: 'bookings', path: '/admin/bookings', icon: CalendarDays },
  { key: 'customers', path: '/admin/customers', icon: Users },
  { key: 'units', path: '/admin/units', icon: Monitor },
  { key: 'addons', path: '/admin/addons', icon: Package },
  { key: 'incidents', path: '/admin/incidents', icon: ShieldAlert },
  { key: 'alerts', path: '/admin/alerts', icon: Bell },
  { key: 'pricing', path: '/admin/pricing', icon: DollarSign },
  { key: 'reports', path: '/admin/reports', icon: BarChart3 },
  { key: 'settings', path: '/admin/settings', icon: Settings, children: [
    { key: 'group_booking', hash: '#booking', icon: Calendar },
    { key: 'group_cancellation', hash: '#cancellation', icon: RotateCcw },
    { key: 'group_loyalty', hash: '#loyalty', icon: Users },
    { key: 'group_notifications', hash: '#notifications', icon: Bell },
    { key: 'group_staff', hash: '#staff', icon: Shield },
    { key: 'group_payments', hash: '#payments', icon: CreditCard },
    { key: 'group_locales', hash: '#locales', icon: Globe },
  ]},
] as const;

export default function AdminLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const updateProfile = useUpdateProfile();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Discoverability: a count badge on the Incidents nav item for items needing operator
  // attention (queued approvals + denied-pending-manual). Polled by the same hook the page uses.
  const { data: incidents } = useIncidents();
  const incidentAttentionCount =
    (incidents?.queuedCompensations.length ?? 0) + (incidents?.pendingManualResolution.length ?? 0);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleLocale = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    // Same fire-and-forget profile write as CustomerLayout / VenueNavbar
    // (PR IV) — closes the same drift bug on the operator surface. UI
    // doesn't wait for the API; an API failure logs but does not revert.
    // See doc/DECISIONS.md "Language Switcher Single Source of Truth".
    if (isAuthenticated) {
      updateProfile.mutate(
        { locale: newLang },
        {
          onError: (err) => {
            console.warn('Failed to persist language preference to profile', err);
          },
        },
      );
    }
  };

  const initials = user
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : '';

  const isActive = (path: string) =>
    path === '/admin'
      ? location.pathname === '/admin'
      : location.pathname.startsWith(path);

  const sidebarContent = (
    <nav className="flex flex-col gap-1 p-3">
      {NAV_ITEMS.map((item) => {
        const { key, path, icon: Icon } = item;
        const children = 'children' in item ? item.children : undefined;
        const active = isActive(path);
        return (
          <div key={key}>
            <Link
              to={path}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              <span className="truncate">{t(`admin.${key}`)}</span>
              {key === 'incidents' && incidentAttentionCount > 0 && (
                <Badge className="ml-auto bg-[--color-brand] text-white text-[10px]">
                  {incidentAttentionCount}
                </Badge>
              )}
            </Link>
            {active && children && (
              <div className="ml-5 mt-0.5 flex flex-col gap-0.5 border-l border-border pl-3">
                {children.map((child) => {
                  const ChildIcon = child.icon;
                  const isChildActive = location.hash === child.hash;
                  return (
                    <Link
                      key={child.key}
                      to={`/admin/settings${child.hash}`}
                      onClick={() => {
                        setSidebarOpen(false);
                        setTimeout(() => {
                          document.getElementById(child.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors',
                        isChildActive
                          ? 'text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <ChildIcon className="h-3 w-3" />
                      <span className="truncate">{t(`admin.${child.key}`)}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-56 shrink-0 border-r bg-card lg:block">
        <div className="flex h-14 items-center border-b px-4">
          <Link to="/admin" className="text-lg font-bold tracking-tight text-primary">
            RekPlay
          </Link>
          <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            Admin
          </span>
        </div>
        {sidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="relative w-64 bg-card h-full">
            <div className="flex h-14 items-center justify-between border-b px-4">
              <span className="text-lg font-bold text-primary">RekPlay</span>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b bg-background/95 px-4 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="hidden lg:block" />

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLocale}
            >
              {i18n.language === 'en' ? 'FR' : 'EN'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
